/******************************************
*  Author : Lorenzo Muscariello
*  Created On : 29 March 2023
*  File : GA4Helper.ts
*******************************************/
import { BrowserLogger } from "../Logging/BrowserLogger.js";
import { isObject } from "../TypeChecking/index.js";
//import { LIB_VERSION } from "../version.js";
import { GA4DeferrableLoader } from "./GA4DeferrableLoader.js";
import { GA4Config } from "./GoogleProductsInitializerConfig.js";
import { StandardUserIdRetriever } from "./StandardUserIdRetriever.js";
const GA4_GLOBAL = "_cpga4";
const GA4_GLOBAL_PARAMS = "_cpga4_params";
/**
 * Classe che funge da helper e wrapper per google analytics 4
 */
export class GA4UnifiedHelper {
    win;
    config;
    logger;
    sharedOpts;
    pageTitle;
    pageLocation;
    deferredG4;
    constructor(win, cfg, logger) {
        this.win = win;
        if (logger) {
            this.logger = logger;
        }
        else {
            this.logger = new BrowserLogger("GA4");
        }
        this.win[GA4_GLOBAL] = this.win[GA4_GLOBAL] || {};
        this.win[GA4_GLOBAL_PARAMS] = this.win[GA4_GLOBAL_PARAMS] || {};
        const deferrable = GA4DeferrableLoader(this.win, cfg.accountId, cfg.defer);
        this.deferredG4 = () => {
            this.logger.debug("running Deferred GA4");
            deferrable();
            this.deferredG4 = null;
        };
        if (cfg instanceof GA4Config) {
            this.config = cfg;
        }
        else {
            this.config = new GA4Config(cfg);
        }
        this.sharedOpts = {
            send_page_view: false,
            // shatsu_version: LIB_VERSION
        };
        if (this.config.applyUserId) {
            this.logger.info("looking for user id to apply");
            const userId = StandardUserIdRetriever();
            if (userId) {
                this.logger.info("Applying user_id", userId);
                this.sharedOpts.user_id = userId;
            }
        }
        //this.sharedOpts.name = this.config.subname;
        this.sharedOpts.cookie_flags = "SameSite=None; Secure";
        this.sharedOpts.cookie_domain = this.config.trackingOptions.useCookieDomain;
        this.logger.debug("Using cookie_domain " + this.config.trackingOptions.useCookieDomain);
    }
    set eventGlobalProperties(props) {
        if (props) {
            this.win[GA4_GLOBAL_PARAMS][this.config.accountId] =
                {
                    ...this.win[GA4_GLOBAL_PARAMS][this.config.accountId],
                    ...props
                };
        }
    }
    get eventGlobalProperties() {
        return this.win[GA4_GLOBAL_PARAMS][this.config.accountId];
    }
    /**
     * configures gtag
     * @param additionalProperties
     */
    configure(additionalProperties) {
        if (!(this.config.accountId in this.win[GA4_GLOBAL])) {
            this.win[GA4_GLOBAL][this.config.accountId] = true;
            this.win[GA4_GLOBAL_PARAMS][this.config.accountId] = {};
            if (this.deferredG4)
                this.deferredG4();
            this.logger.log("Configuring property - " + this.config.accountId);
            const maybePageProps = {};
            if (this.pageLocation)
                maybePageProps.page_location = this.pageLocation;
            if (this.pageTitle)
                maybePageProps.page_title = this.pageTitle;
            this.eventGlobalProperties = additionalProperties;
            this.gtag_raw('js', new Date());
            this.gtag_raw('config', this.config.accountId, this.sharedOpts);
        }
        else if (isObject(additionalProperties) && Object.keys(additionalProperties ? additionalProperties : {}).length > 0) {
            this.logger.log("Property - " + this.config.accountId + " - already configured - setting params", additionalProperties);
            this.eventGlobalProperties = additionalProperties;
        }
    }
    /**
     * Wrapper della funzione gtag
     * @param args
     */
    gtag_raw(...args) {
        this.win['gtag'](...args);
    }
    /**
     * Produce un evento su analytics passando come dati aggiuntivi quelli in parameters (semplice hashmap)
     * @param eventName
     * @param parameters
     */
    sendEvent(eventName, parameters, timeout) {
        this.fireAndForgetEvent(eventName, parameters);
        return Promise.resolve();
    }
    fireAndForgetEvent(eventName, parameters) {
        const baseParams = {};
        if (this.pageTitle) {
            baseParams.page_title = this.pageTitle;
        }
        if (this.pageLocation) {
            baseParams.page_location = this.pageLocation;
        }
        const useParams = {
            ...this.eventGlobalProperties,
            ...baseParams,
            ...parameters
        };
        this.gtag_raw("event", eventName, {
            ...useParams,
            'send_to': this.config.accountId
        });
    }
    /**
     * Sets page title and location
     * @param pageName
     */
    setPage(pageName, pagePath) {
        this.pageTitle = pageName;
        this.pageLocation = pagePath;
    }
    /**
     * Imposta la pagina come pagina corrente ed invia una pageview a gogle analytics.
     * @param props
     * @param page_title Title
     * @param page_location Location
     * @param timeout Promise timeout (in milliseconds)
     */
    sendPageView(props, page_title, page_location, timeout) {
        if (page_title)
            this.pageTitle = page_title;
        if (page_location)
            this.pageLocation = page_location;
        return this.sendEvent("page_view", props, timeout);
    }
    /**
     * sets an user property
     * @param propName
     * @param propValue
     * @returns
     */
    setUserProperty(propName, propValue) {
        const props = { [propName]: propValue };
        return this.setUserProperties(props);
    }
    /**
     * sets user properties
     * @param props
     * @returns
     */
    setUserProperties(props) {
        this.gtag_raw('set', 'user_properties', props);
        return Promise.resolve();
    }
}
